import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { PurchaseOrder } from '../../../../models/PurchaseOrder';
import {
  CreateQuotationDto,
  PurchaseOrderQuotation,
  PurchaseOrderQuotationHeader
} from '../../../../models/PurchaseOrderQuotation';
import { Supplier } from '../../../../models/Supplier';
import { AuthService } from '../../../../services/auth.service';
import { PurchaseOrderService } from '../../../../services/purchase-order.service';
import { PurchaseOrderQuotationsService } from '../../../../services/purchase-order-quotations.service';
import { SuppliersService } from '../../../../services/suppliers.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { CURRENCY_OPTIONS, CurrencyCode } from '../../../../shared/constants/currency';

interface ProposalFormModel {
  supplierId: number | null;
  quotationDate: string;
  validUntil: string;
  currencyCode: string;
  itemsSubtotal: number | null;
  discountAmount: number | null;
  freightAmount: number | null;
  otherCostsAmount: number | null;
  taxAmount: number | null;
  totalAmount: number | null;
  paymentTermDays: number | null;
  deliveryLeadTimeDays: number | null;
  status: string;
  notes: string;
}

@Component({
  selector: 'app-purchase-order-quotation-create',
  templateUrl: './purchase-order-quotation-create.component.html',
  styleUrls: ['./purchase-order-quotation-create.component.css']
})
export class PurchaseOrderQuotationCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  purchaseOrderId: number | null = null;
  purchaseOrder: PurchaseOrder | null = null;
  header: PurchaseOrderQuotationHeader | null = null;
  suppliers: Supplier[] = [];
  /** Propostas já criadas no header atual (usado para evitar fornecedor duplicado). */
  existingProposals: PurchaseOrderQuotation[] = [];
  staffId: number | null = null;

  isLoading = false;
  isSaving = false;

  /** Lista de moedas exposta no template (`*ngFor="let c of currencyOptions"`). */
  readonly currencyOptions = CURRENCY_OPTIONS;

  form: ProposalFormModel = this.emptyForm();

  /** Fornecedores ainda não cotados neste orçamento (header). */
  get availableSuppliers(): Supplier[] {
    const used = this.usedSupplierIds;
    return this.suppliers.filter((s) => !used.has(Number(s.id)));
  }

  /** Conjunto de `supplierId` com proposta ativa (não cancelada) no header. */
  get usedSupplierIds(): Set<number> {
    const set = new Set<number>();
    for (const p of this.existingProposals) {
      if (!this.isProposalCancelled(p)) {
        set.add(Number(p.supplierId));
      }
    }
    return set;
  }

  /** Verdadeiro quando todos os fornecedores cadastrados já estão cotados. */
  get hasNoSuppliersLeft(): boolean {
    return this.suppliers.length > 0 && this.availableSuppliers.length === 0;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private purchaseOrderService: PurchaseOrderService,
    private quotationsService: PurchaseOrderQuotationsService,
    private suppliersService: SuppliersService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.authService.getStaffId().pipe(takeUntil(this.destroy$)).subscribe((id) => (this.staffId = id));

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get('id'));
      if (!Number.isFinite(id) || id <= 0) {
        this.router.navigate(['/purchase-orders']);
        return;
      }
      this.purchaseOrderId = id;
      this.loadPageData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void {
    if (!this.purchaseOrderId) {
      this.router.navigate(['/purchase-orders']);
      return;
    }
    this.router.navigate(['/purchase-orders', this.purchaseOrderId, 'quotations']);
  }

  save(): void {
    if (!this.purchaseOrderId || !this.header) {
      this.errorService.showErrorMessage('Processo de cotação não encontrado para este pedido.');
      return;
    }

    const supplierId = Number(this.form.supplierId || 0);
    if (!supplierId) {
      this.errorService.showErrorMessage('Selecione o fornecedor.');
      return;
    }
    if (this.usedSupplierIds.has(supplierId)) {
      this.errorService.showErrorMessage(
        'Este fornecedor já possui uma proposta neste orçamento. Selecione outro.'
      );
      return;
    }

    const payload: CreateQuotationDto = {
      purchaseOrderQuotationHeaderId: this.header.id,
      supplierId,
      quotationNumber: this.generateQuotationNumber(),
      quotationDate: this.toIso(this.form.quotationDate),
      validUntil: this.toIso(this.form.validUntil),
      currencyCode: (this.form.currencyCode || CurrencyCode.BRL).trim() || CurrencyCode.BRL,
      itemsSubtotal: Number(this.form.itemsSubtotal ?? 0),
      discountAmount: Number(this.form.discountAmount ?? 0),
      freightAmount: Number(this.form.freightAmount ?? 0),
      otherCostsAmount: Number(this.form.otherCostsAmount ?? 0),
      taxAmount: Number(this.form.taxAmount ?? 0),
      totalAmount: Number(this.form.totalAmount ?? this.computeTotal()),
      paymentTermDays: Number(this.form.paymentTermDays ?? 0),
      deliveryLeadTimeDays: Number(this.form.deliveryLeadTimeDays ?? 0),
      status: (this.form.status || 'Recebida').trim() || 'Recebida',
      isWinner: false,
      notes: this.form.notes?.trim() || undefined
    };

    this.isSaving = true;
    this.quotationsService.createQuotation(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSaving = false;
        this.errorService.showSuccessMessage('Proposta criada com sucesso.');
        this.goBack();
      },
      error: () => {
        this.isSaving = false;
        this.errorService.showErrorMessage('Não foi possível criar a proposta.');
      }
    });
  }

  recalculateTotal(): void {
    this.form.totalAmount = this.computeTotal();
  }

  estimatedItemsTotal(): number {
    const items = this.purchaseOrder?.items || [];
    return items.reduce((sum, item) => sum + Number(item.estimatedTotalPrice || 0), 0);
  }

  private loadPageData(): void {
    if (!this.purchaseOrderId) return;
    this.isLoading = true;

    forkJoin({
      order: this.purchaseOrderService.getPurchaseOrderComplete(this.purchaseOrderId).pipe(catchError(() => of(null))),
      suppliers: this.suppliersService.getAllPaged(1, 500).pipe(
        catchError(() => of({ items: [] as Supplier[], page: 1, pageSize: 500, totalItems: 0, totalPages: 0 }))
      )
    })
      .pipe(
        switchMap(({ order, suppliers }) => {
          this.purchaseOrder = order;
          this.suppliers = suppliers.items || [];
          return this.quotationsService.listHeaders(this.purchaseOrderId!, 1, 50);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (page) => {
          const headers = (page.items || []).filter((h) => !this.isHeaderCancelled(h));
          this.header =
            headers.find((h) => this.isHeaderOpen(h)) ??
            headers.sort((a, b) => b.id - a.id)[0] ??
            null;
          this.loadExistingProposals();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorService.showErrorMessage('Não foi possível carregar os dados da proposta.');
        }
      });
  }

  /**
   * Carrega as propostas já criadas no header para evitar duplicar fornecedor.
   * Sem propagação de erro para o toast: a feature de bloqueio cai em "permissiva"
   * (mostra todos os fornecedores) e o backend ainda valida no POST.
   */
  private loadExistingProposals(): void {
    if (!this.header?.id) {
      this.existingProposals = [];
      return;
    }
    this.quotationsService
      .listQuotationsForHeader(this.header.id, 1, 200)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.existingProposals = resp.items || [];
          // Se o usuário já tinha selecionado um fornecedor que entrou em uso enquanto
          // a página estava aberta, limpa para forçar nova escolha.
          if (
            this.form.supplierId != null &&
            this.usedSupplierIds.has(Number(this.form.supplierId))
          ) {
            this.form.supplierId = null;
          }
        },
        error: () => {
          this.existingProposals = [];
        }
      });
  }

  private isProposalCancelled(p: PurchaseOrderQuotation): boolean {
    const s = (p?.status || '').toLowerCase();
    return s === 'cancelada' || s === 'canceled' || s === 'cancelled';
  }

  private computeTotal(): number {
    const sub = Number(this.form.itemsSubtotal ?? 0);
    const disc = Number(this.form.discountAmount ?? 0);
    const freight = Number(this.form.freightAmount ?? 0);
    const other = Number(this.form.otherCostsAmount ?? 0);
    const tax = Number(this.form.taxAmount ?? 0);
    return Math.round((sub - disc + freight + other + tax + Number.EPSILON) * 100) / 100;
  }

  private generateQuotationNumber(): string {
    const rand = Math.floor(100000 + Math.random() * 900000);
    return `PROP-${Date.now()}-${rand}`;
  }

  private emptyForm(): ProposalFormModel {
    const now = new Date();
    const valid = new Date(now);
    valid.setMonth(valid.getMonth() + 1);
    return {
      supplierId: null,
      quotationDate: this.toInput(now),
      validUntil: this.toInput(valid),
      currencyCode: CurrencyCode.BRL,
      itemsSubtotal: null,
      discountAmount: 0,
      freightAmount: null,
      otherCostsAmount: 0,
      taxAmount: null,
      totalAmount: null,
      paymentTermDays: 30,
      deliveryLeadTimeDays: 7,
      status: 'Recebida',
      notes: ''
    };
  }

  private toInput(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`;
  }

  private toIso(localDateTime: string): string {
    const d = new Date(localDateTime);
    if (Number.isNaN(d.getTime())) return new Date().toISOString();
    return d.toISOString();
  }

  private isHeaderCancelled(h?: PurchaseOrderQuotationHeader | null): boolean {
    const s = (h?.status || '').toLowerCase();
    return s === 'cancelado' || s === 'canceled' || s === 'cancelled';
  }

  private isHeaderOpen(h?: PurchaseOrderQuotationHeader | null): boolean {
    const s = (h?.status || '').toLowerCase();
    return s === 'aberto' || s === 'open';
  }
}
